import { CheckCircle } from '@mui/icons-material';
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined';
import { Box, Typography, Tooltip } from '@mui/material';
import tid from '@/common/application/utils/tid';
import type { EquipmentTableData } from '@/equipments/application/types/EquipmentTypes';
import { EquipmentStatusChip } from '@/equipments/presentation/components/EquipmentStatusChip';
import { CertificationWarningIcon } from '@/raptorjob/presentation/components/RaptorJobDetails/components/JobTicketOverview/JobTicketOverviewERP/components/CertificationWarningIcon';
import { STATUS_COLORS } from '@/common/presentation/constants/colors';

interface EquipmentResource {
  type?: {
    id?: number | string | null;
  } | null;
}

interface EquipmentSelectableListItemProps {
  equipment: EquipmentTableData;
  isSelected: boolean;
  isEquipmentCompliant?: (equipment: EquipmentResource) => boolean;
  getEquipmentMissingCerts?: (equipment: EquipmentResource) => string[];
  onClick: () => void;
}

export function EquipmentSelectableListItem({
  equipment,
  isSelected,
  isEquipmentCompliant,
  getEquipmentMissingCerts,
  onClick
}: EquipmentSelectableListItemProps) {
  const equipmentResource: EquipmentResource = { type: equipment.type };
  const isCompliant = isEquipmentCompliant ? isEquipmentCompliant(equipmentResource) : true;
  const missingCerts = getEquipmentMissingCerts ? getEquipmentMissingCerts(equipmentResource) : [];
  const statusName = equipment.status?.name;

  return (
    <Box
      onClick={onClick}
      sx={{
        cursor: 'pointer',
        maxHeight: '48px',
        padding: 1,
        display: 'flex',
        alignItems: 'center',
        borderRadius: '8px',
        border: '1px solid',
        borderColor: (theme) => (isSelected ? theme.palette.primary.main : theme.palette.divider),
        backgroundColor: (theme) => (isSelected ? `${theme.palette.primary.main}0A` : theme.palette.background.paper),
        justifyContent: 'space-between'
      }}
    >
      <Box sx={{ display: 'flex', gap: '8px', alignItems: 'center', overflow: 'hidden' }}>
        <LocalShippingOutlinedIcon color="primary" sx={{ flexShrink: 0 }} />
        {isEquipmentCompliant !== undefined &&
          (isCompliant ? (
            <Tooltip title="Meets all certification requirements" placement="top">
              <CheckCircle
                sx={{ fontSize: 16, color: STATUS_COLORS.SUCCESS_MAIN, flexShrink: 0 }}
                {...tid(`jobs:ticket-details:edit-resources-modal:equipment-compliance-icon-${equipment.id}`)}
              />
            </Tooltip>
          ) : (
            <CertificationWarningIcon
              tooltipText={`Missing certifications: ${missingCerts.join(', ')}`}
              testId={`jobs:ticket-details:edit-resources-modal:equipment-compliance-icon-${equipment.id}`}
            />
          ))}
        <Typography component="p" fontWeight={600} fontSize="14px" noWrap>
          {equipment.name}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
        {statusName && statusName.toLowerCase() !== 'none' && statusName.toLowerCase() !== 'n/a' && (
          <EquipmentStatusChip status={statusName} />
        )}
      </Box>
    </Box>
  );
}
